import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, getReportSchedule } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import DatePicker from '../components/DatePicker';
import PortalIcon from '../components/PortalIcon';
import { useAuth } from '../context/AuthContext';
import { TIERS, formatDate } from '../utils/license';
import './styles.css';
import '../components/PortalUI.css';

function formatDateTime(iso) {
  if (!iso) return 'Aldrig logget ind';
  return new Date(iso).toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' });
}

const TABS = [
  { key: 'gen', label: 'Generelt', icon: 'edit' },
  { key: 'usr', label: 'Brugere', icon: 'org' },
  { key: 'lic', label: 'Licens', icon: 'tag' },
  { key: 'con', label: 'Forbindelser', icon: 'refresh' },
];

// A Trial can be extended by NordComply staff, but never beyond this many days from when it started
// (the backend enforces the same limit).
const TRIAL_MAX_DAYS = 90;
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// asPage: the customer's own page (not a pop-up), with the people of the customer in the Brugere tab (usersSlot) and a way into the
// customer's portal (onOpenPortal).
export default function EditTenantPanel({ tenant, overview, initialTab = 'gen', onClose, onSaved, asPage = false, usersSlot = null, onOpenPortal = null, portalHref = null, backLabel = '' }) {
  const { role } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const lic = tenant.license || null;
  const [tier, setTier] = useState(lic?.tier || 'premium');
  const [trialEnd, setTrialEnd] = useState(lic?.trialEndsAt ? lic.trialEndsAt.slice(0, 10) : '');
  // The calendar for the end date: from today to 90 days after the Trial started (a Trial started now when the
  // tenant is switched to Trial from another licence).
  const trialStart = lic?.tier === 'trial' ? new Date(tenant.trialStartedAt || tenant.addedAt || Date.now()) : new Date();
  const trialMaxIso = isoDay(new Date(trialStart.getFullYear(), trialStart.getMonth(), trialStart.getDate() + TRIAL_MAX_DAYS));
  const todayIso = isoDay(new Date());
  const [savingLicense, setSavingLicense] = useState(false);
  const [licenseError, setLicenseError] = useState('');
  const [licenseSuccess, setLicenseSuccess] = useState('');
  // The tenant prop is refreshed after saving — keep the field in step with it.
  useEffect(() => {
    setTier(lic?.tier || 'premium');
    setTrialEnd(lic?.trialEndsAt ? lic.trialEndsAt.slice(0, 10) : '');
  }, [lic?.tier, lic?.trialEndsAt]);
  const [name, setName] = useState(tenant.name);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [renameSuccess, setRenameSuccess] = useState('');

  const [supportEmail, setSupportEmail] = useState(tenant.supportEmail || '');
  const [supportName, setSupportName] = useState(tenant.supportName || '');
  const [savingSupportEmail, setSavingSupportEmail] = useState(false);
  const [supportEmailError, setSupportEmailError] = useState('');
  const [supportEmailSuccess, setSupportEmailSuccess] = useState('');

  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [removingEmail, setRemovingEmail] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);

  const [syncing, setSyncing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [syncError, setSyncError] = useState('');

  const [reauthing, setReauthing] = useState(false);
  const [reauthError, setReauthError] = useState('');

  const [connectingExchange, setConnectingExchange] = useState(false);
  const [exchangeError, setExchangeError] = useState(tenant.exchangeConnectError || '');

  // null = still loading, [] = loaded and genuinely empty. Only the history (date + recipients) is
  // kept anywhere — the report itself is never stored once sent, per direct instruction.
  const [reportHistory, setReportHistory] = useState(null);
  const [reportHistoryError, setReportHistoryError] = useState('');
  useEffect(() => {
    let cancelled = false;
    getReportSchedule(tenant.tenantId)
      .then((data) => { if (!cancelled) setReportHistory(data.schedule?.sendHistory || []); })
      .catch((err) => { if (!cancelled) setReportHistoryError(err.message); });
    return () => { cancelled = true; };
  }, [tenant.tenantId]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await apiFetch(`/api/users?tenantId=${tenant.tenantId}`);
      if (!res.ok) throw new Error('Kunne ikke hente brugere');
      const data = await res.json();
      setTenantUsers((data.users || []).filter((u) => u.role === 'customer'));
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [tenant.tenantId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleRename(e) {
    e.preventDefault();
    setRenameError('');
    setRenameSuccess('');
    if (!name.trim()) {
      setRenameError('Navn må ikke være tomt');
      return;
    }
    setRenaming(true);
    try {
      const res = await apiFetch('/api/tenant', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenant.tenantId, name: name.trim() }),
      });
      if (!res.ok) throw new Error('Kunne ikke omdøbe tenant');
      setRenameSuccess('Navn opdateret');
      onSaved?.();
    } catch (err) {
      setRenameError(err.message);
    } finally {
      setRenaming(false);
    }
  }

  async function handleSaveSupportEmail(e) {
    e.preventDefault();
    setSupportEmailError('');
    setSupportEmailSuccess('');
    setSavingSupportEmail(true);
    try {
      const res = await apiFetch('/api/tenant', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenant.tenantId, supportEmail: supportEmail.trim(), supportName: supportName.trim() }),
      });
      if (!res.ok) throw new Error('Kunne ikke gemme support-email');
      setSupportEmailSuccess('Gemt');
      onSaved?.();
    } catch (err) {
      setSupportEmailError(err.message);
    } finally {
      setSavingSupportEmail(false);
    }
  }

  async function handleSaveLicense() {
    setLicenseError('');
    setLicenseSuccess('');
    setSavingLicense(true);
    try {
      const res = await apiFetch('/api/tenant', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: tenant.tenantId,
          licenseTier: tier,
          // A date picked for a Trial is the whole day, so it ends at the end of that day.
          ...(tier === 'trial' && trialEnd ? { trialEndsAt: new Date(`${trialEnd}T23:59:59`).toISOString() } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Kunne ikke gemme licens');
      }
      setLicenseSuccess('Gemt');
      onSaved?.();
    } catch (err) {
      setLicenseError(err.message);
    } finally {
      setSavingLicense(false);
    }
  }

  async function confirmRemoveUser() {
    const email = removeTarget;
    setRemovingEmail(email);
    try {
      const res = await apiFetch(`/api/users?email=${encodeURIComponent(email)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Kunne ikke fjerne bruger');
      setTenantUsers((prev) => prev.filter((u) => u.email !== email));
      setRemoveTarget(null);
    } finally {
      setRemovingEmail('');
    }
  }

  async function handleSync() {
    setSyncing(true);
    setSyncError('');
    try {
      const results = await Promise.all([
        apiFetch(`/api/secure-score?tenantId=${tenant.tenantId}`),
        apiFetch(`/api/membership?tenantId=${tenant.tenantId}`),
        apiFetch(`/api/mfa-status?tenantId=${tenant.tenantId}`),
        apiFetch(`/api/conditional-access?tenantId=${tenant.tenantId}`),
      ]);
      const failed = results.some((r) => !r.ok);
      if (failed) throw new Error('Nogle datakilder svarede ikke korrekt');
      setLastChecked(new Date());
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  async function handleConnectExchange() {
    setConnectingExchange(true);
    setExchangeError('');
    try {
      const res = await apiFetch('/api/exchange-connect-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenant.tenantId }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Kunne ikke starte Exchange-forbindelsen');
      }
      const { authUrl } = await res.json();
      // New tab, not window.location — Edge can force-switch to a
      // Microsoft-linked profile when navigating straight to
      // login.microsoftonline.com, which left this panel's own tab on a
      // broken session (reported live). Keeps this tab exactly as it is;
      // the flow still finishes on our own /auth/exchange-callback whatever
      // tab/profile Edge lands it in.
      window.open(authUrl, '_blank', 'noopener');
      setConnectingExchange(false);
    } catch (err) {
      setExchangeError(err.message);
      setConnectingExchange(false);
    }
  }

  async function handleReauthenticate() {
    setReauthing(true);
    setReauthError('');
    try {
      const res = await apiFetch('/auth/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenant.tenantId, friendlyName: tenant.name }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Kunne ikke starte godkendelsen');
      }
      const { authUrl } = await res.json();
      // New tab, not window.location — same Edge profile-switching issue
      // as handleConnectExchange above.
      window.open(authUrl, '_blank', 'noopener');
      setReauthing(false);
    } catch (err) {
      setReauthError(err.message);
      setReauthing(false);
    }
  }

  // What the tenant list shows as the connection state, so the two never disagree.
  const missing = overview?.missingPermissions || [];
  const graph = !overview ? 'checking' : overview.needsReauth ? 'reauth' : overview.connectionOk ? 'ok' : 'bad';
  const exchangeOk = Boolean(tenant.exchangeConnectedAt);
  // Connected before the mail checks existed: the app lacks the roles to read spam filters, DKIM and audit settings.
  const exchangeOutdated = exchangeOk && !(tenant.exchangeRoles || []).includes('View-Only Configuration');
  const conNeedsAttention = graph === 'reauth' || graph === 'bad' || Boolean(exchangeError) || exchangeOutdated;
  const graphChip = { checking: ['Tjekker forbindelse…', 'neutral'], ok: ['Forbundet', 'ok'], reauth: ['Kræver autentificering', 'warn'], bad: ['Fejl ved forbindelse', 'bad'] }[graph];

  const outer = asPage ? { className: 'etp-pagewrap' } : { className: 'modal-overlay', onClick: onClose };
  const panel = asPage ? { className: 'pu-page etp etp--page' } : { className: 'modal-panel modal-panel--wide etp', onClick: (e) => e.stopPropagation() };
  return (
    <div {...outer}>
      <div {...panel}>
        {asPage && (
          <>
            <button type="button" className="pu-back" onClick={onClose}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              {backLabel || 'Kunder'}
            </button>
            <div className="pu-cust">
              <span className="pu-av pu-av--xl">{String(tenant.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}</span>
              <div>
                <h1>{tenant.name}</h1>
                <small>Tilføjet af {tenant.addedByName || tenant.addedByEmail || 'ukendt'} · {formatDateTime(tenant.addedAt)}</small>
              </div>
              <span className="pu-cust__chips" />
              {onOpenPortal && <a href={portalHref || '#'} className="pu-btn pu-btn--p" onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onOpenPortal(); }}>Åbn kundens portal<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>}
            </div>
          </>
        )}
        {!asPage && <div className="modal-panel__head">
          <div className="modal-panel__title">
            <span className="modal-panel__ico"><PortalIcon name="tenant" size={21} /></span>
            <div>
              <h2>{tenant.name}</h2>
              <p className="modal-panel__subtitle">Tilføjet af {tenant.addedByName || tenant.addedByEmail || 'ukendt'} · {formatDateTime(tenant.addedAt)}</p>
            </div>
          </div>
          <button className="modal-panel__close" onClick={onClose} aria-label="Luk">×</button>
        </div>}

        <div className="etp-chips">
          <span className={`etp-chip etp-chip--${graphChip[1]}`}>{graphChip[0]}</span>
          <span className={`etp-chip etp-chip--${exchangeOk && !exchangeOutdated ? 'ok' : 'warn'}`}>{exchangeOutdated ? 'Exchange kræver nyt samtykke' : exchangeOk ? 'Exchange forbundet' : 'Exchange ikke forbundet'}</span>
          {graph === 'ok' && <span className="etp-chip etp-chip--ok">Tilladelser i orden</span>}
        </div>

        <div className="etp-body">
          <nav className="etp-nav" role="tablist" aria-label="Tenant-indstillinger">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? 'on' : ''}
                onClick={() => setTab(t.key)}
              >
                <PortalIcon name={t.icon} size={16} />
                {t.label}
                {t.key === 'con' && conNeedsAttention && <i className="etp-dot" aria-label="Kræver handling" />}
              </button>
            ))}
          </nav>

          <div className={`etp-pane etp-pane--${tab}`}>
            {tab === 'gen' && (
              <>
                <h3 className="etp-h">Generelt</h3>
                <p className="etp-sub">Navn og hvem der modtager kundens henvendelser.</p>
                <section className="etp-card">
                  <h4>Tenant-navn</h4>
                  <p>Sådan vises tenanten i portalen.</p>
                  <form onSubmit={handleRename} className="modal-inline-form">
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={renaming} aria-label="Tenant-navn" />
                    <button type="submit" className="btn-primary" disabled={renaming}>{renaming ? 'Gemmer…' : 'Gem'}</button>
                  </form>
                  {renameError && <p className="modal-msg modal-msg--error">{renameError}</p>}
                  {renameSuccess && <p className="modal-msg modal-msg--success">{renameSuccess}</p>}
                </section>
                <section className="etp-card">
                  <h4>Support / IT-leverandør</h4>
                  <p>Modtager mailen fra IT-opgaver. Navnet vises på knappen: Send til SAC-IT.</p>
                  <form onSubmit={handleSaveSupportEmail} className="modal-inline-form">
                    <input type="text" placeholder="Navn, fx SAC-IT" maxLength={80} value={supportName} onChange={(e) => setSupportName(e.target.value)} disabled={savingSupportEmail} aria-label="Navn på IT-leverandør" />
                    <input type="email" placeholder="support@jeres-msp.dk" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} disabled={savingSupportEmail} aria-label="Support-email" />
                    <button type="submit" className="btn-primary" disabled={savingSupportEmail}>{savingSupportEmail ? 'Gemmer…' : 'Gem'}</button>
                  </form>
                  {supportEmailError && <p className="modal-msg modal-msg--error">{supportEmailError}</p>}
                  {supportEmailSuccess && <p className="modal-msg modal-msg--success">{supportEmailSuccess}</p>}
                </section>
                <section className="etp-card">
                  <h4>Automatiske rapporter</h4>
                  <p>Historik over rapporter sendt til denne tenant. Selve rapporten gemmes ikke — kun hvornår og til hvem.</p>
                  {reportHistoryError && <p className="modal-msg modal-msg--error">{reportHistoryError}</p>}
                  {reportHistory === null && !reportHistoryError && <p className="etp-none">Henter…</p>}
                  {reportHistory?.length === 0 && <p className="etp-none">Ingen rapporter sendt endnu.</p>}
                  {reportHistory?.length > 0 && (
                    <div className="etp-hist">
                      {reportHistory.slice(0, 6).map((h, i) => (
                        <div className="etp-hist__row" key={i}>
                          <span className="etp-hist__ic"><PortalIcon name="check" size={13} /></span>
                          <div className="etp-hist__main">
                            <b>{formatDateTime(h.sentAt)}</b>
                            <span>{(h.recipients || []).join(', ') || 'Ingen modtagere'}</span>
                          </div>
                          <span className={`etp-hist__badge${h.kind === 'test' ? ' is-test' : ''}`}>{h.kind === 'test' ? 'Test' : 'Automatisk'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}

            {tab === 'lic' && (
              <>
                <h3 className="etp-h">Licens</h3>
                <p className="etp-sub">Hvad tenanten har adgang til i NordComply.{role === 'provider' ? '' : ' Kun NordComply kan ændre licensen.'}</p>
                <section className="etp-card">
                  {role === 'provider' ? (
                    <div className="lcp-opts" role="radiogroup" aria-label="Licens">
                      {TIERS.map((t) => (
                        <button key={t.key} type="button" role="radio" aria-checked={tier === t.key} className={`lcp-opt${tier === t.key ? ' is-on' : ''}`} onClick={() => setTier(t.key)} disabled={savingLicense}>
                          <b>{t.label}</b>
                          <span>{t.short}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="lcp-current">
                      <span>Nuværende licens</span>
                      <b className="lic-pill"><PortalIcon name="tag" size={13} />{TIERS.find((t) => t.key === lic?.tier)?.label || '—'}</b>
                    </div>
                  )}

                  {tier === 'trial' && (
                    <div className="lcp-meta">
                      {lic?.tier === 'trial' && lic?.trialEndsAt ? (
                        <span>Udløber: <b>{formatDate(lic.trialEndsAt)}</b>{lic.expired ? ' (udløbet)' : ` (${lic.daysLeft} ${lic.daysLeft === 1 ? 'dag' : 'dage'} tilbage)`}</span>
                      ) : (
                        <span>Starter ved gem og varer 30 dage.</span>
                      )}
                      {role === 'provider' && (
                        <div className="lcp-date">
                          <span>Ny slutdato</span>
                          <DatePicker value={trialEnd} onChange={setTrialEnd} placeholder="Vælg dato" ariaLabel="Ny slutdato for prøveperioden" min={todayIso} max={trialMaxIso} allowClear={false} />
                        </div>
                      )}
                      {role === 'provider' && <span>Højst {TRIAL_MAX_DAYS} dage fra start ({formatDate(`${trialMaxIso}T12:00:00`)}).</span>}
                      <span>Ved udløb: <b>låst, kun login</b></span>
                    </div>
                  )}

                  {(() => {
                    const t = TIERS.find((x) => x.key === tier);
                    const rightList = t.limits || t.missing || null;
                    return (
                      <div className="lcp-detail">
                        <div>
                          <h5>{tier === 'trial' ? 'Adgang' : 'Inkluderet'}</h5>
                          <ul>{t.includes.map((x) => <li key={x}><PortalIcon name="check" size={13} />{x}</li>)}</ul>
                        </div>
                        <div>
                          <h5>{tier === 'trial' ? 'Begrænsninger i Trial' : 'Ikke inkluderet'}</h5>
                          <ul>
                            {rightList
                              ? rightList.map((x) => <li key={x} className={tier === 'trial' ? '' : 'is-no'}>{tier === 'trial' ? <PortalIcon name="check" size={13} /> : <span className="lcp-dash">–</span>}{x}</li>)
                              : <li className="is-no"><span className="lcp-dash">–</span>Intet. Alle funktioner er åbne.</li>}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}

                  {role === 'provider' && (
                    <div className="etp-actions">
                      <button type="button" className="btn-primary" onClick={handleSaveLicense} disabled={savingLicense}>{savingLicense ? 'Gemmer…' : 'Gem licens'}</button>
                    </div>
                  )}
                  {licenseError && <p className="modal-msg modal-msg--error">{licenseError}</p>}
                  {licenseSuccess && <p className="modal-msg modal-msg--success">{licenseSuccess}</p>}
                </section>
              </>
            )}

            {tab === 'con' && (
              <>
                <h3 className="etp-h">Forbindelser</h3>
                <p className="etp-sub">Hvordan NordComply læser data fra kundens Microsoft 365.</p>

                {graph === 'reauth' && (
                  <section className="etp-card etp-card--alert">
                    <h4>Kræver autentificering</h4>
                    <p>Appen har fået nye tilladelser, som denne tenant endnu ikke har godkendt. Nogle data kan derfor ikke hentes. En <b>Global Administrator hos kunden</b> skal godkende.</p>
                    {missing.length > 0 && <div className="etp-perms">{missing.map((m) => <code key={m}>{m}</code>)}</div>}
                    <button className="btn-primary" onClick={handleReauthenticate} disabled={reauthing}>{reauthing ? 'Åbner Microsoft…' : 'Godkend igen →'}</button>
                    {reauthError && <p className="modal-msg modal-msg--error">{reauthError}</p>}
                  </section>
                )}

                <section className="etp-card">
                  <h4>Microsoft 365 (Graph)</h4>
                  <p>Brugere, enheder, politikker og sikkerhed.</p>
                  <div className="etp-kv"><span>Status</span><b className={`etp-tone--${graphChip[1]}`}>{graphChip[0]}</b></div>
                  <div className="etp-kv"><span>Tilladelser</span><b className={missing.length ? 'etp-tone--warn' : ''}>{!overview ? 'Tjekker…' : missing.length ? `${missing.length} mangler` : 'Alle godkendt'}</b></div>
                  {lastChecked && <div className="etp-kv"><span>Sidst tjekket</span><b>kl. {lastChecked.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}</b></div>}
                  <div className="etp-actions">
                    <button className="btn-secondary" onClick={handleSync} disabled={syncing}>{syncing ? 'Tjekker…' : 'Genindlæs data nu'}</button>
                    <button className="btn-secondary" onClick={handleReauthenticate} disabled={reauthing}>{reauthing ? 'Åbner Microsoft…' : 'Genautentificér →'}</button>
                  </div>
                  <p className="etp-foot">Genautentificér bruges, hvis data mangler eller ser forkerte ud. En Global Administrator hos kunden skal logge ind og godkende.</p>
                  {syncError && <p className="modal-msg modal-msg--error">{syncError}</p>}
                  {graph !== 'reauth' && reauthError && <p className="modal-msg modal-msg--error">{reauthError}</p>}
                </section>

                <section className="etp-card">
                  <h4>Exchange Online</h4>
                  <p>Skelner delte postkasser, mødelokaler og udstyr fra rigtige medarbejdere, så de ikke fejlagtigt vises som "mangler MFA" eller "inaktiv konto". Kun læsning.</p>
                  <div className="etp-kv"><span>Status</span>{exchangeOk ? <b className="etp-tone--ok">Forbundet {formatDateTime(tenant.exchangeConnectedAt)}</b> : <b className="etp-tone--warn">Ikke forbundet</b>}</div>
                  {exchangeOutdated && <p className="etp-foot"><b>Kræver nyt samtykke:</b> forbindelsen blev oprettet, før vi læste mailbeskyttelse. Forbind igen, så appen også må læse spamfiltre, DKIM og auditindstillinger. Kun læsning.</p>}
                  <div className="etp-actions">
                    <button className="btn-secondary" onClick={handleConnectExchange} disabled={connectingExchange}>
                      {connectingExchange ? 'Åbner Microsoft…' : exchangeOk ? 'Forbind igen →' : 'Forbind Exchange Online →'}
                    </button>
                  </div>
                  <p className="etp-foot">En Exchange- eller Global Administrator hos kunden skal godkende.</p>
                  {exchangeError && <p className="modal-msg modal-msg--error">{exchangeError}</p>}
                </section>
              </>
            )}

            {tab === 'usr' && usersSlot}
            {tab === 'usr' && !usersSlot && (
              <>
                <h3 className="etp-h">Brugere med adgang</h3>
                <p className="etp-sub">Kundens egne brugere i portalen. Nye tilføjes under Brugere i sidemenuen, med denne tenant valgt.</p>
                {usersLoading && <p className="etp-none">Henter…</p>}
                {usersError && <p className="modal-msg modal-msg--error">{usersError}</p>}
                {!usersLoading && !usersError && tenantUsers.length === 0 && <div className="etp-empty">Ingen kundebrugere oprettet endnu.</div>}
                {!usersLoading && tenantUsers.length > 0 && (
                  <ul className="modal-user-list">
                    {tenantUsers.map((u) => (
                      <li key={u.email} className="modal-user-list__item">
                        <div className="modal-user-list__info">
                          <span className="modal-user-list__name">{u.name}</span>
                          <span className="modal-user-list__email">{u.email}</span>
                        </div>
                        <div className="modal-user-list__meta"><span>{formatDateTime(u.lastLoginAt)}</span></div>
                        <button type="button" className="modal-user-list__remove" onClick={() => setRemoveTarget(u.email)} disabled={removingEmail === u.email} aria-label={`Fjern ${u.name}`} title="Fjern bruger">
                          {removingEmail === u.email ? '…' : '×'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {removeTarget && (
        <ConfirmDialog
          title="Fjern bruger"
          message={`Fjern ${removeTarget} fra kundeportalen?`}
          confirmLabel="Fjern"
          onCancel={() => setRemoveTarget(null)}
          onConfirm={confirmRemoveUser}
        />
      )}
    </div>
  );
}
